import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { triggerHaptic } from '../utils/nativeBridge';

/**
 * Native-feeling Draggable Bottom Sheet modal for mobile devices.
 * Supports swipe-down gesture to dismiss with spring physics,
 * grab handle pill, backdrop blur, and safe area insets.
 */
const BottomSheet = ({
  isOpen,
  onClose,
  title,
  children,
  headerRight,
  maxHeight = 'max-h-[88vh]',
  showHandle = true,
  className = '',
}) => {
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isRendered, setIsRendered] = useState(isOpen);
  const [animatingIn, setAnimatingIn] = useState(false);

  const startYRef = useRef(0);
  const currentYRef = useRef(0);
  const startTimeRef = useRef(0);
  const sheetRef = useRef(null);

  // Mount/Unmount with smooth slide animation
  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      setTranslateY(0);
      // Next tick trigger slide up
      requestAnimationFrame(() => {
        setAnimatingIn(true);
      });
    } else {
      setAnimatingIn(false);
      const timer = setTimeout(() => {
        setIsRendered(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleDismiss = useCallback(() => {
    triggerHaptic('light');
    setAnimatingIn(false);
    setTimeout(() => {
      onClose();
    }, 200);
  }, [onClose]);

  // Touch Drag Handling for Sheet Dismiss
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    startYRef.current = touch.clientY;
    currentYRef.current = touch.clientY;
    startTimeRef.current = Date.now();
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    currentYRef.current = touch.clientY;
    const deltaY = currentYRef.current - startYRef.current;

    // Only allow pulling downwards
    if (deltaY > 0) {
      setTranslateY(deltaY);
    } else {
      // Slight upward resistance (rubber band)
      setTranslateY(deltaY * 0.15);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const deltaY = currentYRef.current - startYRef.current;
    const duration = Date.now() - startTimeRef.current;
    const velocity = deltaY / Math.max(duration, 1); // px per ms

    // Dismiss if pulled down > 90px or quick swipe down (velocity > 0.45)
    if (deltaY > 90 || velocity > 0.45) {
      setTranslateY(window.innerHeight);
      handleDismiss();
    } else {
      // Spring back up
      setTranslateY(0);
    }
  };

  if (!isRendered) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 select-none"
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      {/* Dimmed backdrop */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-200 ${
          animatingIn ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleDismiss}
        aria-hidden="true"
      />

      {/* Sheet / Dialog Card */}
      <div
        ref={sheetRef}
        style={{
          transform: animatingIn
            ? `translate3d(0, ${translateY}px, 0)`
            : 'translate3d(0, 100%, 0)',
          transition: isDragging ? 'none' : 'transform 0.26s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        className={`relative w-full sm:max-w-lg bg-white dark:bg-zinc-950 rounded-t-3xl sm:rounded-2xl shadow-2xl flex flex-col border border-zinc-200 dark:border-zinc-800 ${maxHeight} z-10 overflow-hidden pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-0 ${className}`}
      >
        {/* Grab Handle Header (Touch gesture target) */}
        <div
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="pt-2.5 pb-2 px-4 flex flex-col items-center cursor-grab active:cursor-grabbing select-none shrink-0"
        >
          {showHandle && (
            <div className="w-10 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full mb-2" />
          )}

          {(title || headerRight) && (
            <div className="w-full flex items-center justify-between mt-1">
              <div className="w-8" />
              {typeof title === 'string' ? (
                <h3 className="font-bold text-zinc-900 dark:text-zinc-100 text-base text-center">
                  {title}
                </h3>
              ) : (
                title
              )}
              <div className="w-8 flex justify-end">
                {headerRight || (
                  <button
                    type="button"
                    onClick={handleDismiss}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                  >
                    <i className="fas fa-times text-xs" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sheet Content */}
        <div className="flex-1 overflow-y-auto px-4 py-2 min-h-0">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BottomSheet;
